
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Feedback } from "@/types";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { canUseChangelogEmails } from "@/utils/planLimits";

type ChangelogFormData = {
  title: string;
};

export const ChangelogForm = ({ 
  companyId,
  onSuccess,
  companyPlan = 'free'
}: { 
  companyId: string;
  onSuccess: () => void;
  companyPlan?: string;
}) => {
  const { register, handleSubmit, reset } = useForm<ChangelogFormData>();
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [completedFeedback, setCompletedFeedback] = useState<Feedback[]>([]);
  const [lastChangelogDate, setLastChangelogDate] = useState<string | null>(null);

  useEffect(() => {
    fetchCompletedFeedbackSinceLastChangelog();
  }, [companyId]);

  const fetchCompletedFeedbackSinceLastChangelog = async () => {
    try {
      const { data: changelogs, error: changelogError } = await supabase
        .from('changelogs')
        .select('created_at')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (changelogError) throw changelogError;
      
      let sinceDate = '1970-01-01T00:00:00Z';

      if (changelogs && changelogs.length > 0) {
        sinceDate = changelogs[0].created_at;
        setLastChangelogDate(sinceDate);
      }

      const { data: feedbackData, error: feedbackError } = await supabase
        .from('feedback')
        .select('*')
        .eq('company_id', companyId)
        .eq('status', 'completed')
        .gt('updated_at', sinceDate)
        .order('updated_at', { ascending: false });

      if (feedbackError) throw feedbackError;
      
      if (feedbackData) {
        const formattedFeedback = feedbackData.map(item => ({
          ...item,
          votes: 0, // Add missing property
          hasVoted: false, // Add missing property
          score_method: (item.score_method as 'value_effort' | 'rice' | undefined) || 'value_effort'
        }));
        setCompletedFeedback(formattedFeedback);
      } else {
        setCompletedFeedback([]);
      }
    } catch (error) {
      console.error('Error fetching completed feedback:', error);
    }
  };

  const handleCopyFeedback = (feedback: Feedback) => {
    const feedbackText = `- ${feedback.title}\n`;
    setDescription(prev => prev + feedbackText);
    toast.success('Added to changelog content');
  };

  const onSubmit = async (data: ChangelogFormData) => {
    if (!description.trim()) {
      toast.error('Description is required');
      return;
    }

    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: changelog, error } = await supabase
        .from('changelogs')
        .insert({
          ...data,
          description,
          type: 'feature',
          company_id: companyId,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) throw error;

      // Check if the plan allows changelog emails
      const emailCheck = canUseChangelogEmails(companyPlan);
      if (emailCheck.allowed) {
        const { error: emailError } = await supabase.functions.invoke('send-changelog-email', {
          body: { changelogId: changelog.id }
        });

        if (emailError) {
          console.error('Error sending emails:', emailError);
          toast.error('Changelog created but failed to send notification emails');
        } else {
          toast.success('Changelog added and notification emails sent');
        }
      } else {
        toast.success('Changelog added (Email notifications available on Starter and Enterprise plans)');
      }

      reset();
      setDescription('');
      fetchCompletedFeedbackSinceLastChangelog();
      onSuccess();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to add changelog');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input
        placeholder="Changelog title"
        {...register('title', { required: true })}
      />
      
      <div className="space-y-2">
        <RichTextEditor
          value={description}
          onChange={setDescription}
          placeholder="Describe your changelog... Add features, bug fixes, and improvements."
          className="min-h-[200px]"
        />
      </div>
      
      <Button type="submit" disabled={isLoading}>
        <Plus className="w-4 h-4 mr-2" />
        Add Changelog
      </Button>

      {completedFeedback.length > 0 && (
        <div className="mt-6 border rounded-lg p-4 bg-muted/20">
          <h3 className="text-sm font-medium mb-2">
            Completed feedback items since last changelog 
            {lastChangelogDate && (
              <span className="text-xs font-normal text-muted-foreground ml-2">
                ({new Date(lastChangelogDate).toLocaleDateString()})
              </span>
            )}
          </h3>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {completedFeedback.map((item) => (
              <div key={item.id} className="flex items-center justify-between bg-background p-2 rounded-md">
                <div className="text-sm truncate flex-1">{item.title}</div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => handleCopyFeedback(item)}
                >
                  Add to changelog
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </form>
  );
};
